import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, fmtTime, fmtUsd } from "@/lib/api";
import { SectionTitle, Badge } from "@/components/Bits";
import { Zap, RefreshCw, ShieldAlert, AlertTriangle, CheckCircle2, XCircle, Clock3, Unlock } from "lucide-react";

const EXCHANGES = [
  { id: "paper", label: "Paper Trading" },
  { id: "binance_testnet", label: "Binance Testnet" },
  { id: "binance_live", label: "Binance Live" },
  { id: "lbank", label: "LBank Live" },
];

const STATUS_MESSAGES = {
  // The connection-status tile renders ONE of these. Most of the hints
  // were originally written assuming Binance Testnet. They're now
  // exchange-aware: a `make` function fills in the right wording / URL
  // based on the active connection (`conn`). When a static description
  // is enough, we keep the plain object for readability.
  connected: { icon: CheckCircle2, kind: "on", title: "Connected",
    make: (conn) => ({ hint: conn?.exchange === "binance_live"
      ? "Binance Live account reachable. Test connection is read-only — no orders placed."
      : "Testnet account reachable. Ready to place tiny test orders." }) },
  invalid_credentials: { icon: XCircle, kind: "off", title: "Invalid credentials",
    make: (conn) => ({ hint: conn?.exchange === "binance_live"
      ? "API-key format invalid or signature mismatch. Re-create the key at binance.com → API Management."
      : "API-key format invalid or signature mismatch. Re-create the key at testnet.binancefuture.com → API Management." }) },
  futures_disabled: { icon: XCircle, kind: "off", title: "Futures disabled on account",
    make: (conn) => ({ hint: conn?.exchange === "binance_live"
      ? "This Binance account does not have USDT-M Futures enabled. Enable Futures first."
      : "This account does not have USDT-M Futures enabled on testnet. Open a Futures account on testnet first." }) },
  permissions_missing: { icon: XCircle, kind: "off", title: "Missing permissions",
    hint: "API key cannot trade. Enable the \"Futures\" permission when creating the key." },
  hedge_mode_mismatch: { icon: AlertTriangle, kind: "warn", title: "Hedge-mode mismatch",
    hint: "Requested positionSide doesn't match account setting. Enable hedge mode on this panel first." },
  timestamp_drift: { icon: AlertTriangle, kind: "warn", title: "Clock drift",
    hint: "Server clock skew > recvWindow. We auto-resync and retry, but if this persists check the container clock." },
  ip_restricted: { icon: XCircle, kind: "off", title: "IP restricted",
    make: (conn) => ({ hint: conn?.exchange === "binance_live"
      ? "API key only allows whitelisted IPs. Add this server's egress IP at binance.com → API Management → Edit IP access restriction."
      : "API key only allows whitelisted IPs. Remove IP restriction on testnet or add this server's egress IP." }) },
  rate_limited: { icon: AlertTriangle, kind: "warn", title: "Rate-limited",
    hint: "Binance temporarily blocked us for exceeding weight limits. Back off for 60s and retry." },
  invalid_precision: { icon: AlertTriangle, kind: "warn", title: "Invalid quantity/price precision",
    hint: "Order quantity didn't fit Binance's stepSize / minNotional. Increase notional." },
  insufficient_margin: { icon: AlertTriangle, kind: "warn", title: "Insufficient margin",
    make: (conn) => ({ hint: conn?.exchange === "binance_live"
      ? "Wallet balance too low for this notional + leverage."
      : "Testnet wallet balance too low for this notional + leverage." }) },
  server_error: { icon: AlertTriangle, kind: "warn", title: "Binance server error",
    hint: "Transient HTTP 5xx from Binance. We already retried with backoff — try again in a moment." },
  network_error: { icon: AlertTriangle, kind: "warn", title: "Network error",
    make: (conn) => ({ hint: conn?.exchange === "binance_live"
      ? "Timed out reaching Binance. Check container egress / proxy."
      : "Timed out reaching Binance testnet. Check container egress." }) },
  unreachable: { icon: XCircle, kind: "off",
    // `unreachable` is the failure mode the user actually saw with the
    // misleading "Testnet unreachable" copy — make it exchange-aware.
    // The backend now puts the actual base_url into `last_test_message`
    // and `last_test_status_code=unreachable`, so we surface that.
    make: (conn) => ({
      title: conn?.exchange === "binance_live" ? "Binance Live unreachable"
           : conn?.exchange === "binance_testnet" ? "Testnet unreachable"
           : "Exchange unreachable",
      hint: conn?.exchange === "binance_live"
        ? "Failed to reach Binance Futures. The exact endpoint hit is in 'Binance said' below — verify it matches BINANCE_LIVE_FAPI_BASE. HTTP 451 means geo-block; try fapi1/fapi2 or a proxy."
        : "Failed to reach testnet.binancefuture.com.",
    }) },
  error: { icon: XCircle, kind: "off", title: "Error", hint: "See message for details." },
  paper_mode: { icon: CheckCircle2, kind: "warn", title: "Paper mode",
    hint: "Simulated orders only. No exchange connection required." },
  not_connected: { icon: XCircle, kind: "off", title: "Not connected",
    make: (conn) => ({ hint: conn?.exchange === "binance_live"
      ? "Save your Binance Live API key + secret in the per-user card below, then click Test connection."
      : "Save API key + secret, then click Test connection." }) },
};

const EXEC_FILTERS = [
  { k: "all", label: "All" },
  { k: "ok", label: "Success", query: { ok: true } },
  { k: "failed", label: "Failed", query: { ok: false } },
  { k: "place_order", label: "Orders placed", query: { action: "place_order" } },
  { k: "close_position", label: "Closes", query: { action: "close_position" } },
  { k: "sync_positions", label: "Syncs", query: { action: "sync_positions" } },
  { k: "cancel_all_orders", label: "Cancels", query: { action: "cancel_all_orders" } },
];

function resolveStatus(conn) {
  // The original lookup returned the static object; the new schema lets
  // each entry carry an optional `make(conn)` returning overrides. We
  // resolve them here so the JSX consumers stay unchanged.
  const apply = (base) => {
    if (!base) return STATUS_MESSAGES.not_connected;
    if (typeof base.make === "function") {
      const overrides = base.make(conn) || {};
      return { ...base, ...overrides };
    }
    return base;
  };
  if (!conn) return apply(STATUS_MESSAGES.not_connected);
  const code = conn.last_test_status_code;
  if (code && STATUS_MESSAGES[code]) return apply(STATUS_MESSAGES[code]);
  return apply(STATUS_MESSAGES[conn.status] || STATUS_MESSAGES.not_connected);
}

export default function ExchangeConnection() {
  const [conn, setConn] = useState(null);
  const [available, setAvailable] = useState({});
  // Per-exchange disable reason from the backend (e.g.
  //   "LIVE_TRADING_ENABLED=false on the server"
  //   "BINANCE_LIVE_API_KEY / BINANCE_LIVE_API_SECRET missing"
  // ). Surfaced in the dropdown so the user knows exactly what to fix.
  const [exchangeMeta, setExchangeMeta] = useState({});
  // Server-side `LIVE_TRADING_ENABLED` flag, surfaced by /api/exchanges.
  // Drives the dynamic warning text on the per-user credentials card.
  const [liveTradingEnabled, setLiveTradingEnabled] = useState(false);
  const [form, setForm] = useState({ exchange: "paper", api_key: "", api_secret: "", environment: "" });
  const [testBusy, setTestBusy] = useState(false);
  const [orderBusy, setOrderBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [testOrder, setTestOrder] = useState({ side: "LONG", notional_usdt: 30, leverage: 3 });
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [openPositions, setOpenPositions] = useState([]);
  const [botStatus, setBotStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("all");

  // ---- User-scoped Binance Live credentials ----
  const [liveCreds, setLiveCreds] = useState(null);          // {configured, masked_api_key, updated_at}
  const [liveCredsForm, setLiveCredsForm] = useState({ api_key: "", api_secret: "" });
  const [liveCredsBusy, setLiveCredsBusy] = useState(false);

  // ---- User-scoped LBank credentials (parallel to Binance Live) ----
  const [lbankCreds, setLbankCreds] = useState(null);
  const [lbankCredsForm, setLbankCredsForm] = useState({ api_key: "", api_secret: "" });
  const [lbankCredsBusy, setLbankCredsBusy] = useState(false);

  const refreshLiveCreds = async () => {
    try { setLiveCreds(await api.getBinanceLiveCredentials()); } catch {}
  };
  const refreshLbankCreds = async () => {
    try { setLbankCreds(await api.getLbankCredentials()); } catch {}
  };

  const saveLiveCreds = async () => {
    if (!liveCredsForm.api_key || !liveCredsForm.api_secret) {
      toast.error("API Key and API Secret are both required.");
      return;
    }
    setLiveCredsBusy(true);
    try {
      await api.saveBinanceLiveCredentials(liveCredsForm.api_key.trim(), liveCredsForm.api_secret.trim());
      toast.success("Binance Live credentials saved.");
      setLiveCredsForm({ api_key: "", api_secret: "" });
      await refreshLiveCreds();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || e.message));
    } finally { setLiveCredsBusy(false); }
  };

  const deleteLiveCreds = async () => {
    if (!window.confirm("Delete your saved Binance Live API key + secret from this server?\n\nThe bot will fall back to the global env vars (if any) until new keys are saved.")) return;
    setLiveCredsBusy(true);
    try {
      await api.deleteBinanceLiveCredentials();
      toast.success("Binance Live credentials deleted.");
      await refreshLiveCreds();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || e.message));
    } finally { setLiveCredsBusy(false); }
  };

  const saveLbankCreds = async () => {
    if (!lbankCredsForm.api_key || !lbankCredsForm.api_secret) {
      toast.error("API Key and API Secret are both required.");
      return;
    }
    setLbankCredsBusy(true);
    try {
      await api.saveLbankCredentials(lbankCredsForm.api_key.trim(), lbankCredsForm.api_secret.trim());
      toast.success("LBank credentials saved.");
      setLbankCredsForm({ api_key: "", api_secret: "" });
      await refreshLbankCreds();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || e.message));
    } finally { setLbankCredsBusy(false); }
  };

  const deleteLbankCreds = async () => {
    if (!window.confirm("Delete your saved LBank API key + secret from this server?\n\nThe bot will fall back to the global env vars (if any) until new keys are saved.")) return;
    setLbankCredsBusy(true);
    try {
      await api.deleteLbankCredentials();
      toast.success("LBank credentials deleted.");
      await refreshLbankCreds();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || e.message));
    } finally { setLbankCredsBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [c, ex, s] = await Promise.all([api.getConnection(), api.exchanges(), api.status()]);
        setConn(c);
        const avail = {}; const meta = {};
        (ex.exchanges || []).forEach((e) => {
          avail[e.id] = e.available;
          meta[e.id] = { label: e.name, reason: e.reason || null,
                         supports_real_orders: e.supports_real_orders,
                         execution_status: e.execution_status || null };
        });
        setAvailable(avail);
        setExchangeMeta(meta);
        setLiveTradingEnabled(Boolean(ex.live_trading_enabled));
        setForm((f) => ({ ...f, exchange: c.exchange }));
        setBotStatus(s);
      } catch {}
    })();
  }, []);

  const refreshConn = async () => {
    const [c, s] = await Promise.all([api.getConnection(), api.status()]);
    setConn(c); setBotStatus(s);
  };
  const refreshOpenPositions = async () => {
    try { setOpenPositions(await api.openPositions()); } catch {}
  };

  const loadLogs = async (filterKey = logFilter) => {
    try {
      const f = EXEC_FILTERS.find((x) => x.k === filterKey);
      setLogs(await api.testnetExecutionLogs(50, f?.query || {}));
    } catch {}
  };

  useEffect(() => { loadLogs(logFilter); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [logFilter]);

  const save = async () => {
    try {
      await api.putConnection(form);
      toast.success("Saved");
      setForm((f) => ({ ...f, api_secret: "" }));
      await refreshConn();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const test = async () => {
    setTestBusy(true);
    try {
      const r = await api.testConnection();
      r.success ? toast.success(r.message) : toast.error(r.message);
      await refreshConn();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setTestBusy(false); }
  };

  // Explicit per-exchange probe handlers used by the per-user credentials
  // cards. They pass `?exchange=` so the backend ignores whatever is
  // currently saved in `exchange_connection.exchange` and probes the
  // requested exchange instead. The saved-connection status is NOT
  // mutated by the backend in this path — it's a read-only probe.
  const testBinanceLiveCredentials = async () => {
    setTestBusy(true);
    try {
      const r = await api.testConnection("binance_live");
      const label = `[${r.tested_exchange || "binance_live"}] ${r.message}`;
      r.success ? toast.success(label) : toast.error(label);
      await refreshConn();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setTestBusy(false); }
  };

  const testLbankCredentials = async () => {
    setTestBusy(true);
    try {
      const r = await api.testConnection("lbank");
      const label = `[${r.tested_exchange || "lbank"}] ${r.message}`;
      r.success ? toast.success(label) : toast.error(label);
      await refreshConn();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setTestBusy(false); }
  };

  // Hedge mode is controlled on the exchange directly — this panel only reads it.

  // Whenever bot settings poll returns, or the connection test runs, refresh
  // the open-positions list so the user sees real state.
  useEffect(() => { if (form.exchange === "binance_testnet") refreshOpenPositions(); }, [form.exchange, conn?.last_sync_at]);

  // When the user selects Binance Live, load the saved-credential status
  // so the UI can render "Configured ✓ (•••• 1234)" or "Not configured".
  useEffect(() => {
    if (form.exchange === "binance_live") refreshLiveCreds();
    if (form.exchange === "lbank") refreshLbankCreds();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [form.exchange]);

  const openPreview = async () => {
    setPreviewBusy(true);
    try {
      const p = await api.testnetPreviewOrder({ side: testOrder.side, notional_usdt: testOrder.notional_usdt, leverage: testOrder.leverage });
      setPreview(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setPreviewBusy(false); }
  };

  const placeOrder = async () => {
    if (!preview) return;
    setOrderBusy(true);
    try {
      const r = await api.testnetPlaceTestOrder({ ...testOrder, confirm: true });
      toast.success(`Order placed · latency ${r.latency_ms}ms${r.retry_count ? ` · ${r.retry_count} retry` : ""}`);
      setPreview(null);
      await loadLogs();
      await refreshOpenPositions();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || e.message));
    } finally { setOrderBusy(false); }
  };

  const closeManual = async (id) => {
    if (!window.confirm("Close this testnet position with a MARKET order?")) return;
    try {
      const r = await api.testnetClosePosition(id);
      toast.success(`Closed · PnL ${r.pnl_usdt?.toFixed(2)} USDT · latency ${r.latency_ms}ms`);
      await refreshOpenPositions();
      await loadLogs();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || e.message));
    }
  };

  const unlockValidationMode = async () => {
    if (!window.confirm("Unlock automated testnet execution?\n\nThis allows the signal engine to place real orders on Binance testnet. Only proceed after you have completed the manual validation checklist (connection, hedge-mode, order, sync, avg-fill, close, kill-switch, rejected-order).")) return;
    try {
      // PUT to bot settings endpoint, preserving everything else
      const cur = botStatus?.bot_settings || {};
      await api.putBotSettings({ ...cur, testnet_validation_mode: false });
      toast.success("Validation mode OFF — automated testnet execution enabled");
      refreshConn();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const syncPositions = async () => {
    setSyncBusy(true);
    try {
      const r = await api.testnetSyncPositions();
      const m = r.mismatches?.length || 0;
      m === 0 ? toast.success(`Synced · ${r.exchange_positions.length} exchange position(s), in sync`) : toast.error(`${m} desync(s) detected — check execution logs`);
      await refreshConn();
      await loadLogs();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSyncBusy(false); }
  };

  const isTestnet = form.exchange === "binance_testnet";
  const isLive = form.exchange === "binance_live";
  const isLbank = form.exchange === "lbank";
  const status = resolveStatus(conn);
  const StatusIcon = status.icon;
  const desyncCount = conn?.last_sync_mismatches?.length || 0;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <SectionTitle sub="Choose how the bot connects to the market. API secrets are stored on the server and never sent back to the browser.">
        Exchange Connection
      </SectionTitle>

      {isTestnet ? (
        <div className="rounded-sm border border-[#F59E0B]/60 bg-[#F59E0B]/10 px-4 py-3 flex items-start gap-3" data-testid="testnet-warning-banner">
          <AlertTriangle size={18} strokeWidth={2} className="text-[#F59E0B] shrink-0 mt-0.5" />
          <div className="flex-1 text-xs leading-relaxed">
            <div className="text-sm font-semibold text-[#F59E0B] uppercase tracking-wider mb-0.5">Testnet mode · Play money only</div>
            <div className="text-[#A0A0AB]">
              This panel controls a <span className="font-mono">testnet.binancefuture.com</span> account.
              Orders are real on testnet but the funds are fake. Binance production orders are
              permanently blocked by <span className="font-mono">LIVE_TRADING_ENABLED=false</span> —
              when live mode is ever enabled in the future, the UI will colour it red, not yellow.
            </div>
          </div>
        </div>
      ) : null}

      <div className={`bg-[#0F0F14] border rounded-sm p-5 space-y-4 ${status.kind === "on" ? "border-[#00E676]/40" : status.kind === "warn" ? "border-[#F59E0B]/40" : "border-[#FF3B30]/40"}`} data-testid="connection-status-tile">
        <div className="flex items-start gap-4">
          <StatusIcon size={22} strokeWidth={1.75} className={status.kind === "on" ? "text-[#00E676]" : status.kind === "warn" ? "text-[#F59E0B]" : "text-[#FF3B30]"} />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold" data-testid="connection-status-title">{status.title}</div>
                <div className="text-xs text-[#A0A0AB] mt-0.5">{status.hint}</div>
              </div>
              <Badge kind={status.kind} testid="connection-status-badge">{conn?.status?.replace("_", " ").toUpperCase() || "—"}</Badge>
            </div>
            {conn?.last_test_message ? (
              <div className="text-[11px] font-mono text-[#A0A0AB] mt-2 border-t border-border pt-2" data-testid="connection-last-message">
                {conn?.last_tested_exchange && conn.last_tested_exchange !== conn?.exchange ? (
                  <span className="text-[#F59E0B]" data-testid="connection-last-tested-exchange-mismatch">
                    [probe · tested {conn.last_tested_exchange}, saved {conn.exchange}]{" "}
                  </span>
                ) : conn?.last_tested_exchange ? (
                  <span className="text-[#71717A]" data-testid="connection-last-tested-exchange">
                    [{conn.last_tested_exchange}]{" "}
                  </span>
                ) : null}
                {conn.last_test_message}
              </div>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px]">Exchange</div><div className="font-mono mt-0.5">{conn?.exchange}</div></div>
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px]">API key</div><div className="font-mono mt-0.5">{conn?.api_key_masked || "—"}</div></div>
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px]">Wallet balance</div><div className="font-mono mt-0.5">{conn?.last_total_wallet_balance != null ? `${conn.last_total_wallet_balance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT` : "—"}</div></div>
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px]">Margin balance</div><div className="font-mono mt-0.5">{conn?.last_total_margin_balance != null ? `${conn.last_total_margin_balance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT` : "—"}</div></div>
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px]">Available</div><div className="font-mono mt-0.5">{conn?.last_available_balance != null ? `${conn.last_available_balance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT` : "—"}</div></div>
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px]">Unrealized PnL</div><div className={`font-mono mt-0.5 ${conn?.last_total_unrealized_pnl > 0 ? "text-[#00E676]" : conn?.last_total_unrealized_pnl < 0 ? "text-[#FF3B30]" : ""}`}>{conn?.last_total_unrealized_pnl != null ? `${conn.last_total_unrealized_pnl >= 0 ? "+" : ""}${conn.last_total_unrealized_pnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT` : "—"}</div></div>
          <div>
            <div className="text-[#71717A] uppercase tracking-widest text-[9px] flex items-center justify-between">
              <span>Hedge mode</span>
              {isTestnet ? (
                <button data-testid="refresh-hedge-btn" onClick={async () => {
                  try { const r = await api.testnetRefreshHedgeMode(); if (r.ok) { toast.success(`Hedge mode: ${r.hedge_mode ? "ON" : "OFF"}`); refreshConn(); } else { toast.error("Failed to read hedge mode"); } } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                }} className="text-[9px] underline decoration-dotted hover:text-white" title="Repoll Binance for current hedge-mode setting">refresh</button>
              ) : null}
            </div>
            <div className="font-mono mt-0.5">{conn?.last_test_hedge_mode === true ? <span className="text-[#00E676]">ON</span> : conn?.last_test_hedge_mode === false ? <span className="text-[#FF3B30]">OFF</span> : "—"}</div>
          </div>
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px] inline-flex items-center gap-1"><Clock3 size={9} /> Last test</div><div className="font-mono mt-0.5">{fmtTime(conn?.last_test_at) || "—"}</div></div>
        </div>

        {/* Asset breakdown */}
        {conn?.last_assets && conn.last_assets.length > 0 ? (
          <div className="border-t border-border pt-3 space-y-1.5" data-testid="asset-breakdown">
            <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Asset breakdown</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono">
                <thead className="text-[9px] uppercase tracking-widest text-[#71717A]">
                  <tr><th className="text-left py-1">Asset</th><th className="text-right py-1">Wallet</th><th className="text-right py-1">uPnL</th><th className="text-right py-1">Margin</th><th className="text-right py-1">Available</th></tr>
                </thead>
                <tbody>
                  {conn.last_assets.map((a) => (
                    <tr key={a.asset} className="border-t border-border/60">
                      <td className="py-1">{a.asset}</td>
                      <td className="text-right">{a.wallet_balance?.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      <td className={`text-right ${a.unrealized_pnl > 0 ? "text-[#00E676]" : a.unrealized_pnl < 0 ? "text-[#FF3B30]" : ""}`}>{a.unrealized_pnl >= 0 ? "+" : ""}{a.unrealized_pnl?.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      <td className="text-right">{a.margin_balance?.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      <td className="text-right">{a.available_balance?.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* Sync grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs border-t border-border pt-3">
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px] inline-flex items-center gap-1"><Clock3 size={9} /> Last sync</div><div className="font-mono mt-0.5">{fmtTime(conn?.last_sync_at) || "—"}</div></div>
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px]">Local open</div><div className="font-mono mt-0.5">{conn?.last_sync_local_count ?? "—"}</div></div>
          <div><div className="text-[#71717A] uppercase tracking-widest text-[9px]">Exchange open</div><div className="font-mono mt-0.5 flex items-center gap-1.5">
            {conn?.last_sync_exchange_count ?? "—"}
            {desyncCount > 0 ? <span className="text-[10px] px-1.5 py-0.5 rounded-sm border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF7A6E]">{desyncCount} desync</span> : null}
          </div></div>
        </div>
      </div>

      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-5">
        <div className="text-sm uppercase tracking-widest text-[#A0A0AB]">Configure</div>

        <div className="space-y-2">
          <Label>Exchange</Label>
          <Select value={form.exchange} onValueChange={(v) => setForm({ ...form, exchange: v })}>
            <SelectTrigger data-testid="exchange-select" className="bg-[#1A1A22] border-border rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXCHANGES.map((e) => {
                const meta = exchangeMeta[e.id] || {};
                const isDisabled = available[e.id] === false;
                // Prefer the backend-supplied label so it can flag
                // "(read-only)" honestly for LBank etc.
                const label = meta.label || e.label;
                let suffix = "";
                if (isDisabled && meta.reason) suffix = ` — ${meta.reason}`;
                else if (isDisabled) suffix = " (live disabled on server)";
                return (
                  <SelectItem key={e.id} value={e.id} disabled={isDisabled}
                              data-testid={`exchange-option-${e.id}`}>
                    {label}{suffix}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!isLive && !isLbank ? (
            <>
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  data-testid="api-key-input" value={form.api_key}
                  onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  placeholder="leave blank to keep existing"
                  className="bg-[#1A1A22] border-border rounded-sm font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>API Secret</Label>
                <Input
                  data-testid="api-secret-input" type="password" value={form.api_secret}
                  onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
                  placeholder="leave blank to keep existing"
                  className="bg-[#1A1A22] border-border rounded-sm font-mono"
                />
              </div>
            </>
          ) : (
            <div className="md:col-span-2 text-[11px] text-[#A0A0AB] bg-[#1A1A22] border border-border rounded-sm p-3 leading-relaxed" data-testid="live-creds-redirect-hint">
              For {isLive ? "Binance Live" : "LBank Live"}, save your API Key + Secret in the encrypted per-user card below. The shared API-key fields are hidden because Live credentials are stored per logged-in user, not in the shared connection config.
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button data-testid="save-connection-btn" onClick={save} className="rounded-sm bg-white text-black hover:bg-[#A0A0AB]">
            Save
          </Button>
          {!isLive && !isLbank ? (
            <Button data-testid="test-connection-btn" variant="outline" onClick={test} disabled={testBusy} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
              {testBusy ? "Testing…" : "Test connection"}
            </Button>
          ) : null}
        </div>
        {isTestnet ? (
          <div className="text-[11px] text-[#F59E0B] border-t border-border pt-3 leading-relaxed">
            Get Testnet API keys at <span className="underline">testnet.binancefuture.com → API Key → Create</span>. Enable &quot;Futures&quot; permission. No IP restriction required for development.
          </div>
        ) : null}
      </div>

      {isLive ? (
        <div className="bg-[#0F0F14] border border-[#FF3B30]/40 rounded-sm p-5 space-y-4" data-testid="binance-live-credentials-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium inline-flex items-center gap-2">
                <ShieldAlert size={14} strokeWidth={2} className="text-[#FF3B30]" />
                Binance Live API credentials (per-user)
              </div>
              <div className="text-xs text-[#A0A0AB] mt-1 leading-relaxed">
                Stored encrypted on the server (Fernet · AES-128-CBC + HMAC). The secret is never returned to the browser.{" "}
                {liveTradingEnabled ? (
                  <span data-testid="live-trading-warning-on">
                    <span className="text-[#FF7A6E] font-medium">Live trading is enabled.</span>{" "}
                    Test connection is read-only and will not place orders.{" "}
                    <span className="text-[#FF7A6E]">Never grant Withdrawal permission</span> when creating the API key.
                  </span>
                ) : (
                  <span data-testid="live-trading-warning-off">
                    Live trading is blocked by the server kill-switch (<span className="font-mono">LIVE_TRADING_ENABLED=false</span>). Test connection is read-only.
                  </span>
                )}
              </div>
            </div>
            {liveCreds?.configured ? (
              <Badge kind="on" testid="live-creds-status-badge">CONFIGURED</Badge>
            ) : (
              <Badge kind="off" testid="live-creds-status-badge">NOT CONFIGURED</Badge>
            )}
          </div>

          {liveCreds?.configured ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs border-t border-border pt-3">
              <div>
                <div className="text-[#71717A] uppercase tracking-widest text-[9px]">Saved API key</div>
                <div className="font-mono mt-0.5" data-testid="live-creds-masked-key">{liveCreds.masked_api_key || "—"}</div>
              </div>
              <div>
                <div className="text-[#71717A] uppercase tracking-widest text-[9px]">Last updated</div>
                <div className="font-mono mt-0.5">{fmtTime(liveCreds.updated_at) || "—"}</div>
              </div>
              <div className="md:text-right">
                <Button data-testid="live-creds-delete-btn" variant="outline"
                  disabled={liveCredsBusy} onClick={deleteLiveCreds}
                  className="rounded-sm border-[#FF3B30]/50 bg-[#1A1A22] hover:bg-[#FF3B30]/10 text-[#FF7A6E]">
                  Delete saved keys
                </Button>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-3">
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input
                data-testid="live-creds-api-key-input"
                value={liveCredsForm.api_key}
                onChange={(e) => setLiveCredsForm({ ...liveCredsForm, api_key: e.target.value })}
                placeholder={liveCreds?.configured ? "leave blank to keep current key" : "paste your Binance Live API key"}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>API Secret</Label>
              <Input
                data-testid="live-creds-api-secret-input"
                type="password"
                value={liveCredsForm.api_secret}
                onChange={(e) => setLiveCredsForm({ ...liveCredsForm, api_secret: e.target.value })}
                placeholder={liveCreds?.configured ? "leave blank to keep current secret" : "paste your Binance Live API secret"}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button data-testid="live-creds-save-btn"
              disabled={liveCredsBusy || !liveCredsForm.api_key || !liveCredsForm.api_secret}
              onClick={saveLiveCreds}
              className="rounded-sm bg-white text-black hover:bg-[#A0A0AB]">
              {liveCredsBusy ? "Saving…" : (liveCreds?.configured ? "Replace credentials" : "Save credentials")}
            </Button>
            <Button data-testid="live-creds-test-btn" variant="outline"
              disabled={testBusy || !liveCreds?.configured} onClick={testBinanceLiveCredentials}
              className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
              {testBusy ? "Testing…" : "Test connection (read-only)"}
            </Button>
          </div>

          <div className="text-[11px] text-[#A0A0AB] border-t border-border pt-3 leading-relaxed">
            Get keys at <span className="font-mono">binance.com → API Management → Create API</span>. Enable the <span className="font-mono">Futures</span> permission. <span className="text-[#FF7A6E]">Never grant Withdrawal scope.</span> If your account is geo-restricted, the test will return an HTTP 451-style error from Binance.
          </div>
        </div>
      ) : null}

      {isLbank ? (
        <div className="bg-[#0F0F14] border border-[#FF3B30]/40 rounded-sm p-5 space-y-4" data-testid="lbank-credentials-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium inline-flex items-center gap-2">
                <ShieldAlert size={14} strokeWidth={2} className="text-[#FF3B30]" />
                LBank API credentials (per-user)
              </div>
              <div className="text-xs text-[#A0A0AB] mt-1 leading-relaxed">
                Stored encrypted on the server (Fernet · AES-128-CBC + HMAC). The secret is never returned to the browser.{" "}
                {liveTradingEnabled ? (
                  <span data-testid="lbank-live-trading-warning-on">
                    <span className="text-[#FF7A6E] font-medium">Live trading is enabled.</span>{" "}
                    Test connection is read-only and will not place orders.{" "}
                    <span className="text-[#FF7A6E]">Never grant Withdrawal permission</span> when creating the API key.
                  </span>
                ) : (
                  <span data-testid="lbank-live-trading-warning-off">
                    Live trading is blocked by the server kill-switch (<span className="font-mono">LIVE_TRADING_ENABLED=false</span>). Test connection is read-only.
                  </span>
                )}
              </div>
            </div>
            {lbankCreds?.configured ? (
              <Badge kind="on" testid="lbank-creds-status-badge">CONFIGURED</Badge>
            ) : (
              <Badge kind="off" testid="lbank-creds-status-badge">NOT CONFIGURED</Badge>
            )}
          </div>

          {lbankCreds?.configured ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs border-t border-border pt-3">
              <div>
                <div className="text-[#71717A] uppercase tracking-widest text-[9px]">Saved API key</div>
                <div className="font-mono mt-0.5" data-testid="lbank-creds-masked-key">{lbankCreds.masked_api_key || "—"}</div>
              </div>
              <div>
                <div className="text-[#71717A] uppercase tracking-widest text-[9px]">Last updated</div>
                <div className="font-mono mt-0.5">{fmtTime(lbankCreds.updated_at) || "—"}</div>
              </div>
              <div className="md:text-right">
                <Button data-testid="lbank-creds-delete-btn" variant="outline"
                  disabled={lbankCredsBusy} onClick={deleteLbankCreds}
                  className="rounded-sm border-[#FF3B30]/50 bg-[#1A1A22] hover:bg-[#FF3B30]/10 text-[#FF7A6E]">
                  Delete saved keys
                </Button>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-3">
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input
                data-testid="lbank-creds-api-key-input"
                value={lbankCredsForm.api_key}
                onChange={(e) => setLbankCredsForm({ ...lbankCredsForm, api_key: e.target.value })}
                placeholder={lbankCreds?.configured ? "leave blank to keep current key" : "paste your LBank API key"}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>API Secret</Label>
              <Input
                data-testid="lbank-creds-api-secret-input"
                type="password"
                value={lbankCredsForm.api_secret}
                onChange={(e) => setLbankCredsForm({ ...lbankCredsForm, api_secret: e.target.value })}
                placeholder={lbankCreds?.configured ? "leave blank to keep current secret" : "paste your LBank API secret"}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button data-testid="lbank-creds-save-btn"
              disabled={lbankCredsBusy || !lbankCredsForm.api_key || !lbankCredsForm.api_secret}
              onClick={saveLbankCreds}
              className="rounded-sm bg-white text-black hover:bg-[#A0A0AB]">
              {lbankCredsBusy ? "Saving…" : (lbankCreds?.configured ? "Replace credentials" : "Save credentials")}
            </Button>
            <Button data-testid="lbank-creds-test-btn" variant="outline"
              disabled={testBusy || !lbankCreds?.configured} onClick={testLbankCredentials}
              className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
              {testBusy ? "Testing…" : "Test connection (read-only)"}
            </Button>
          </div>

          <div className="text-[11px] text-[#A0A0AB] border-t border-border pt-3 leading-relaxed">
            Get keys at <span className="font-mono">lbank.com → User Center → API Management → Create</span>. Enable the <span className="font-mono">Contract</span> (USDT-M Perpetual) permission. <span className="text-[#FF7A6E]">Never grant Withdrawal scope.</span> Symbol used by the bot is <span className="font-mono">BTCUSDT</span>.
          </div>
        </div>
      ) : null}

      {isTestnet && conn?.status === "connected" ? (
        <div className="bg-[#0F0F14] border border-[#F59E0B]/30 rounded-sm p-5 space-y-5" data-testid="testnet-ops-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium inline-flex items-center gap-2"><Zap size={14} strokeWidth={2} className="text-[#F59E0B]" /> Testnet Execution</div>
              <div className="text-xs text-[#A0A0AB] mt-1">Validate the full pipeline with tiny orders on Binance Futures testnet. Position sync runs automatically every 30s.</div>
            </div>
            {botStatus?.bot_settings?.testnet_validation_mode ? (
              <Badge kind="warn" testid="validation-mode-badge">VALIDATION MODE</Badge>
            ) : (
              <Badge kind="on" testid="validation-mode-badge">AUTO EXECUTION ENABLED</Badge>
            )}
          </div>

          {botStatus?.bot_settings?.testnet_validation_mode ? (
            <div className="rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-3 py-2.5 text-[11px] text-[#A0A0AB]" data-testid="validation-mode-banner">
              <div className="text-[#F59E0B] font-semibold uppercase tracking-widest text-[10px] mb-1">Manual validation mode</div>
              Automated signals cannot place orders on testnet. Only the <span className="font-mono">Place Tiny Test Order</span> button works. Caps while in this mode: BTCUSDT only · max 3× · max 60 USDT notional. Open-position caps are controlled by Risk Settings.
              <div className="mt-2">
                <button data-testid="unlock-validation-btn" onClick={unlockValidationMode} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#F59E0B] hover:text-white underline decoration-dotted">
                  <Unlock size={10} /> I've completed validation — unlock auto execution
                </button>
              </div>
            </div>
          ) : null}

          <div className="rounded-sm border border-border bg-[#0A0A10]/50 px-3 py-2.5 text-[11px] text-[#A0A0AB] leading-relaxed" data-testid="hedge-mode-note">
            <span className="text-[#F59E0B] uppercase tracking-widest text-[10px] mr-1.5">Hedge mode</span>
            controlled by the exchange, not this app. Set it on
            <span className="font-mono"> testnet.binancefuture.com → Preferences → Position Mode → Hedge Mode</span>.
            This panel only reads the current state. Orders are blocked when hedge mode is OFF.
          </div>

          <div className="grid grid-cols-1">
            <Button data-testid="testnet-sync-btn" variant="outline" disabled={syncBusy} onClick={syncPositions} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
              <RefreshCw size={14} className={`mr-2 ${syncBusy ? "animate-spin" : ""}`} /> Sync positions now
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-2">
              <Label>Side</Label>
              <Select value={testOrder.side} onValueChange={(v) => setTestOrder({ ...testOrder, side: v })}>
                <SelectTrigger data-testid="testnet-order-side" className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LONG">LONG</SelectItem>
                  <SelectItem value="SHORT">SHORT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notional (USDT) · max 60</Label>
              <Input data-testid="testnet-order-notional" type="number" step="5" min="5" max="60" value={testOrder.notional_usdt}
                onChange={(e) => setTestOrder({ ...testOrder, notional_usdt: Math.min(60, parseFloat(e.target.value || "0")) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono" />
            </div>
            <div className="space-y-2">
              <Label>Leverage · max 3×</Label>
              <Input data-testid="testnet-order-leverage" type="number" min="1" max="3" value={testOrder.leverage}
                onChange={(e) => setTestOrder({ ...testOrder, leverage: Math.min(3, parseInt(e.target.value || "1", 10)) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono" />
            </div>
            <Button data-testid="testnet-place-order-btn" disabled={previewBusy || orderBusy} onClick={openPreview} className="rounded-sm bg-[#F59E0B] text-black hover:bg-[#F5B038]">
              {previewBusy ? "Preparing…" : "Place Tiny Test Order"}
            </Button>
          </div>

          {openPositions.filter((p) => p.exchange === "binance_testnet").length > 0 ? (
            <div className="border-t border-border pt-4">
              <div className="text-xs uppercase tracking-widest text-[#A0A0AB] mb-2">Open testnet positions</div>
              <div className="bg-[#05050A] border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-[11px]" data-testid="testnet-open-positions-table">
                  <thead className="text-[9px] uppercase tracking-widest text-[#71717A]">
                    <tr>
                      <th className="text-left px-2 py-1.5">Symbol</th>
                      <th className="text-left px-2 py-1.5">Side</th>
                      <th className="text-left px-2 py-1.5">Size</th>
                      <th className="text-left px-2 py-1.5">Entry</th>
                      <th className="text-left px-2 py-1.5">Leverage</th>
                      <th className="text-left px-2 py-1.5">Margin</th>
                      <th className="text-right px-2 py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {openPositions.filter((p) => p.exchange === "binance_testnet").map((p) => (
                      <tr key={p.id} className="border-t border-border/60">
                        <td className="px-2 py-1.5 font-mono">{p.symbol}</td>
                        <td className="px-2 py-1.5"><Badge kind={p.side === "LONG" ? "on" : "warn"}>{p.side}</Badge></td>
                        <td className="px-2 py-1.5 font-mono">{Number(p.size).toFixed(6)}</td>
                        <td className="px-2 py-1.5 font-mono">${Number(p.entry_price).toLocaleString()}</td>
                        <td className="px-2 py-1.5 font-mono">{p.leverage}×</td>
                        <td className="px-2 py-1.5 font-mono">{fmtUsd(p.margin_used)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Button data-testid={`close-position-${p.id}`} size="sm" onClick={() => closeManual(p.id)} className="rounded-sm bg-[#FF3B30] text-white hover:bg-[#D63029] h-7 px-2">Close</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {isTestnet ? (
        <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium">Execution logs</div>
            <div className="flex items-center gap-1 flex-wrap" data-testid="exec-log-filters">
              {EXEC_FILTERS.map((f) => (
                <button key={f.k}
                  data-testid={`exec-filter-${f.k}`}
                  onClick={() => setLogFilter(f.k)}
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-widest rounded-sm border ${
                    logFilter === f.k
                      ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]"
                      : "border-border bg-[#1A1A22] text-[#A0A0AB] hover:text-white"
                  }`}>
                  {f.label}
                </button>
              ))}
              <button data-testid="refresh-logs-btn" onClick={() => loadLogs()} className="text-[10px] uppercase tracking-widest text-[#A0A0AB] hover:text-white px-2 py-1 ml-2">Refresh</button>
            </div>
          </div>
          <div className="bg-[#05050A] border border-border rounded-sm max-h-[28rem] overflow-auto">
            {logs.length === 0 ? (
              <div className="p-3 text-xs text-[#71717A]">No logs for this filter.</div>
            ) : (
              <table className="w-full text-[11px]" data-testid="execution-logs-table">
                <thead className="text-[9px] uppercase tracking-widest text-[#71717A] sticky top-0 bg-[#05050A]">
                  <tr>
                    <th className="text-left px-2 py-1.5">When</th>
                    <th className="text-left px-2 py-1.5">Action</th>
                    <th className="text-left px-2 py-1.5">Side</th>
                    <th className="text-left px-2 py-1.5">Qty</th>
                    <th className="text-left px-2 py-1.5">Latency</th>
                    <th className="text-left px-2 py-1.5">Retries</th>
                    <th className="text-left px-2 py-1.5">OK</th>
                    <th className="text-left px-2 py-1.5">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className={`border-t border-border/60 align-top ${l.ok ? "" : "bg-[#FF3B30]/5"}`}>
                      <td className="px-2 py-1.5 font-mono text-[#A0A0AB] whitespace-nowrap">{fmtTime(l.timestamp)}</td>
                      <td className="px-2 py-1.5">{l.action}</td>
                      <td className="px-2 py-1.5">{l.side || "—"}</td>
                      <td className="px-2 py-1.5 font-mono">{l.qty ?? "—"}</td>
                      <td className="px-2 py-1.5 font-mono">{l.latency_ms}ms</td>
                      <td className="px-2 py-1.5 font-mono">{l.retry_count ?? 0}</td>
                      <td className="px-2 py-1.5">{l.ok ? <span className="text-[#00E676]">✓</span> : <span className="text-[#FF3B30]">✗</span>}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-[#A0A0AB]">
                        {l.response?.data?.orderId ? `orderId: ${l.response.data.orderId}` :
                         l.response?.message ? l.response.message :
                         l.action === "sync_positions" ? `local=${l.response?.local_count} exch=${l.response?.exchange_positions} mism=${l.response?.mismatches?.length || 0}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      <div className="text-xs text-[#71717A] flex items-start gap-2" data-testid="exchange-footer-warning">
        <ShieldAlert size={12} className="mt-0.5 text-[#FF3B30]" />
        {isLbank ? (
          <span>Real LBank production orders are blocked by default (<span className="font-mono">LIVE_TRADING_ENABLED=false</span>). Withdrawal scope must never be granted on any key.</span>
        ) : isLive ? (
          <span>Real Binance production orders are blocked by default (<span className="font-mono">LIVE_TRADING_ENABLED=false</span>). Withdrawal scope must never be granted on any key.</span>
        ) : (
          <span>Real production orders (Binance Live, LBank) are blocked by default (<span className="font-mono">LIVE_TRADING_ENABLED=false</span>). Withdrawal scope must never be granted on any key, testnet or live.</span>
        )}
      </div>

      {/* Confirmation dialog with liquidation estimate */}
      <Dialog open={Boolean(preview)} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent data-testid="order-confirm-dialog" className="bg-[#0F0F14] border-[#F59E0B]/50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#F59E0B]">
              <AlertTriangle size={18} /> Confirm testnet order execution
            </DialogTitle>
            <DialogDescription className="text-[#A0A0AB]">
              Review carefully before sending. Order hits <span className="font-mono">testnet.binancefuture.com</span> with fake money.
            </DialogDescription>
          </DialogHeader>
          {preview ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 font-mono text-xs bg-[#05050A] border border-border rounded-sm p-3">
                <div><span className="text-[#71717A]">Symbol</span><div>{preview.symbol}</div></div>
                <div><span className="text-[#71717A]">Side</span><div className={preview.side === "LONG" ? "text-[#00E676]" : "text-[#FF3B30]"}>{preview.side}</div></div>
                <div><span className="text-[#71717A]">Leverage</span><div>{preview.leverage}×</div></div>
                <div><span className="text-[#71717A]">Notional</span><div>{fmtUsd(preview.notional_usdt)}</div></div>
                <div><span className="text-[#71717A]">Mark price</span><div>{fmtUsd(preview.mark_price_usdt)}</div></div>
                <div><span className="text-[#71717A]">Estimated size</span><div>{preview.estimated_size_btc.toFixed(6)} BTC</div></div>
                <div><span className="text-[#71717A]">Margin used</span><div>{fmtUsd(preview.estimated_margin_usdt)}</div></div>
                <div><span className="text-[#71717A]">Hedge mode</span><div>{preview.hedge_mode_on ? "ON" : "OFF"}</div></div>
                <div className="col-span-2 border-t border-border pt-2">
                  <span className="text-[#71717A]">Est. liquidation price</span>
                  <div className="text-[#FF7A6E] text-sm">{fmtUsd(preview.estimated_liquidation_price_usdt)} ({preview.liquidation_distance_percent.toFixed(1)}% away)</div>
                </div>
              </div>
              <div className="space-y-1.5">
                {(preview.warnings || []).map((w, i) => (
                  <div key={`w${i}`} className="text-[11px] text-[#F59E0B] flex items-start gap-2">
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}
                  </div>
                ))}
                {(preview.blockers || []).map((b, i) => (
                  <div key={`b${i}`} className="text-[11px] text-[#FF3B30] flex items-start gap-2 font-semibold">
                    <XCircle size={11} className="mt-0.5 shrink-0" /> {b}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button data-testid="order-confirm-cancel" variant="outline" onClick={() => setPreview(null)} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
              Cancel
            </Button>
            <Button data-testid="order-confirm-submit"
              onClick={placeOrder}
              disabled={orderBusy || Boolean(preview?.blocked)}
              className="rounded-sm bg-[#F59E0B] text-black hover:bg-[#F5B038]">
              {orderBusy ? "Placing…" : "Confirm & place order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
